import { useEffect } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2,
  GraduationCap,
  Bug,
  CalendarCheck,
  ChartLine,
  ClipboardCheck,
  ClipboardList,
  Megaphone,
  Columns2,
  Droplets,
  FileText,
  FileUp,
  LandPlot,
  LayoutDashboard,
  ListTree,
  Map as MapIcon,
  PencilLine,
  Database,
  LogIn,
  LogOut,
  ShieldCheck,
  Sprout,
  type LucideIcon,
} from 'lucide-react';
import { useTrack } from '../data/TrackContext';
import { analysisNavigationQuery } from '../astra/context';
import { useAnalysisContext } from '../../analysis/context';
import { Icon } from '../ui/Icon';
import { usePersona } from '../auth/RoleContext';
import { personaLabel, roleSpec } from '../auth/personas';
import { NAV_VERSION } from './navVersion';
import { defineMessages, type PlainMessage as MessageDescriptor } from '../../i18n/define';
import { LanguageSwitcher, useT } from '../../i18n';
import { COMMON } from '../../i18n/common';
import { intentHandlers, prefetchIdle, prefetchPage } from '../../api/prefetch';
import { preloadRoute } from './routePreload';

interface NavItem {
  to: string;
  /** Подпись пункта — сообщение каталога (`shell.nav.*`), не строка. */
  label: MessageDescriptor;
  icon: LucideIcon;
  end?: boolean;
}

/**
 * Подписи пунктов меню. `defaultMessage` — русский источник; его же читает
 * индексатор ИИ (`scripts/build-ai-surface.mjs`, меню v2 для тура гида).
 */
const nav = defineMessages({
  seasonPicture: { id: 'shell.nav.seasonPicture', defaultMessage: 'Картина сезона' },
  districtOverview: { id: 'shell.nav.districtOverview', defaultMessage: 'Обзор района' },
  analyst: { id: 'shell.nav.analyst', defaultMessage: 'Аналитик' },
  compare: { id: 'shell.nav.compare', defaultMessage: 'Сравнение' },
  contours: { id: 'shell.nav.contours', defaultMessage: 'Контуры' },
  cycles: { id: 'shell.nav.cycles', defaultMessage: 'Агроциклы' },
  dataMap: { id: 'shell.nav.dataMap', defaultMessage: 'Карта базы района' },
  dataQuality: { id: 'shell.nav.dataQuality', defaultMessage: 'Качество данных' },
  farms: { id: 'shell.nav.farms', defaultMessage: 'Хозяйства' },
  guide: { id: 'shell.nav.guide', defaultMessage: 'Практика работы' },
  map: { id: 'shell.nav.map', defaultMessage: 'Карта' },
  myFields: { id: 'shell.nav.myFields', defaultMessage: 'Мои поля' },
  myReports: { id: 'shell.nav.myReports', defaultMessage: 'Отчёты' },
  today: { id: 'shell.nav.today', defaultMessage: 'Сегодня' },
  assignments: { id: 'shell.nav.assignments', defaultMessage: 'Поручения' },
  entry: { id: 'shell.nav.entry', defaultMessage: 'Ввод за хозяйство' },
  placement: { id: 'shell.nav.placement', defaultMessage: 'Размещение' },
  waterFile: { id: 'shell.nav.waterFile', defaultMessage: 'Файл ирригации' },
  quarantineEntry: { id: 'shell.nav.quarantineEntry', defaultMessage: 'Ввод карантина' },
  templates: { id: 'shell.nav.templates', defaultMessage: 'Шаблоны культур' },
  campaigns: { id: 'shell.nav.campaigns', defaultMessage: 'Кампании' },
  verifyQueue: { id: 'shell.nav.verifyQueue', defaultMessage: 'Очередь проверки' },
  districtRegistry: { id: 'shell.nav.districtRegistry', defaultMessage: 'Реестр района' },
  planning: { id: 'shell.nav.planning', defaultMessage: 'Планирование' },
  quarantine: { id: 'shell.nav.quarantine', defaultMessage: 'Карантин' },
  dataSources: { id: 'shell.nav.dataSources', defaultMessage: 'Источники данных' },
  registryV1: { id: 'shell.nav.registryV1', defaultMessage: 'Что вошло в базу района' },
  briefing: { id: 'shell.nav.briefing', defaultMessage: 'К совещанию' },
  water: { id: 'shell.nav.water', defaultMessage: 'Вода' },
  irrigation: { id: 'shell.nav.irrigation', defaultMessage: 'Ирригация' },
});

const text = defineMessages({
  groupLandV1: { id: 'shell.group.dataAndTerritory', defaultMessage: 'Данные и территория' },
  groupLand: { id: 'shell.group.farmsAndLand', defaultMessage: 'Хозяйства и земля' },
  groupManagement: { id: 'shell.group.management', defaultMessage: 'Управление' },
  groupReference: { id: 'shell.group.reference', defaultMessage: 'Справка' },
  groupLearning: { id: 'shell.group.learning', defaultMessage: 'Обучение' },
  groupDataSources: { id: 'shell.group.dataSources', defaultMessage: 'Источники данных' },
  groupDataAdmin: { id: 'shell.group.dataAdmin', defaultMessage: 'Администрирование данных' },
  navLabel: { id: 'shell.nav.ariaLabel', defaultMessage: 'Разделы', description: 'Подпись навигации для экранного диктора' },
  subStation: { id: 'shell.brand.station', defaultMessage: 'Аналитическая станция' },
  subFarm: { id: 'shell.brand.farmCabinet', defaultMessage: 'Кабинет хозяйства' },
  guestRole: { id: 'shell.persona.guest', defaultMessage: 'Чтение района без входа' },
});

/** Порядок и имена пунктов станции зафиксированы брифом и DESIGN.md. */
const STATION_NAV: NavItem[] = [
  { to: '/', label: nav.seasonPicture, icon: LayoutDashboard, end: true },
  { to: '/cycles', label: nav.cycles, icon: Sprout },
  { to: '/planning', label: nav.planning, icon: CalendarCheck },
  { to: '/map', label: nav.map, icon: MapIcon },
  { to: '/farms', label: nav.farms, icon: Building2 },
  { to: '/contours', label: nav.contours, icon: LandPlot },
  { to: '/water', label: nav.irrigation, icon: Droplets },
  { to: '/quarantine', label: nav.quarantine, icon: Bug },
  { to: '/analytics', label: nav.analyst, icon: ChartLine },
  { to: '/reports/briefing', label: nav.briefing, icon: FileText },
  { to: '/compare', label: nav.compare, icon: Columns2 },
];

/**
 * Раздел диспетчера (роль канона `verifier`): то, где управление пишет, а не
 * только смотрит.
 *
 * Кампаний здесь НЕТ, и это не упущение. Спека `astra-product-surface`,
 * сценарий «Вход диспетчера»: «домом становится очередь проверки, управление
 * кампаниями не показывается». Раньше пункт стоял именно тут — то есть
 * единственной ролью, которая видела кампании, был диспетчер, ровно вопреки
 * спеке и proposal'у («диспетчер — без управления кампаниями»).
 */
const OPS_NAV: NavItem[] = [
  { to: '/ops/assignments', label: nav.assignments, icon: ClipboardList },
  { to: '/ops/verify', label: nav.verifyQueue, icon: ClipboardCheck },
  { to: '/ops/entry', label: nav.entry, icon: PencilLine },
  { to: '/ops/templates', label: nav.templates, icon: ListTree },
  { to: '/ops/placement', label: nav.placement, icon: Sprout },
  // Решение владельца №796 (07.10): кампании запускает и переносит менеджер;
  // экран на живом /campaign-desk, в меню возвращён 12.10 (журнал №1870).
  { to: '/ops/campaigns', label: nav.campaigns, icon: Megaphone },
  { to: '/ops/water', label: nav.waterFile, icon: FileUp },
  // Решение владельца 07.10: менеджер вносит карантинные записи по актам.
  { to: '/ops/quarantine', label: nav.quarantineEntry, icon: Bug },
];

/** Инспектор и ответственный карантина: рабочее место ввода (волна 2б). */
const Q_OPS_NAV: NavItem[] = [{ to: '/ops/quarantine', label: nav.quarantineEntry, icon: Bug }];

/**
 * Пункт «Кампании» снят с меню 03.10 (agroai-0yt.2): экран живёт на фикстурах
 * прототипа. Ниже — история того, почему он вообще стоял у хокимията.
 *
 * Хоким тоже ставит поручения — это его управленческий цикл «увидел →
 * поручил → проконтролировал». Очередь проверки и ввод за хозяйство остаются
 * диспетчерскими: маршрут без пункта в меню — недостижимый маршрут.
 *
 * Кампании переехали сюда ПОД ДОПУЩЕНИЕМ (правка по ревью, вынесено
 * владельцу): спека запрещает показывать их диспетчеру, а правка владельца
 * «ни один экран Astra не исчезает» запрещает делать `/ops/campaigns`
 * недостижимым. Единственное, что удовлетворяет обоим, — отдать пункт
 * хокимияту, чья роль канона (`hokimiyat_manager`) и заводит кампании.
 * Канонического источника у экрана нет вовсе (`routes.ts`:
 * `no-canonical-source`), поэтому прав это не раздаёт — только показ.
 */
const HOKIM_OPS_NAV: NavItem[] = [
  { to: '/ops/assignments', label: nav.assignments, icon: ClipboardList },
  { to: '/ops/templates', label: nav.templates, icon: ListTree },
];

/**
 * Экраны на фикстурах прототипа — другой мир данных и другая дата (26 авг
 * вместо даты канона): «Разбор данных» (`/explore`), «Техника»
 * (`/equipment`); «Кампании» (`/ops/campaigns`) стояли здесь до 12.10 — экран
 * переведён на живой `/campaign-desk` и вернулся в меню менеджера (№1870). Из меню они убраны
 * (agroai-0yt.2, решение владельца 03.10): рядом с каноническими экранами их
 * числа читались как числа района. Маршруты НЕ удалены — экраны открываются
 * по прямому адресу, и вернуть пункт можно одной строкой.
 */
export const HIDDEN_PROTOTYPE_ROUTES = ['/explore', '/equipment'] as const;

/**
 * Раздел «Данные» — канонические экраны, оставшиеся от прежнего фронта.
 *
 * Они отвечают на вопрос, которого у экранов Astra нет вообще: как собран
 * канон, что в него НЕ вошло и где геометрия спорит с реестром. Убрать их
 * значило бы потерять единственный вход в происхождение цифр, поэтому они
 * стоят отдельным разделом, а не растворяются в станции.
 *
 * Карта здесь своя: `/map` занят картой Astra, и канонический MapCanvas живёт
 * по собственному адресу, пока фаза 2 не сведёт оба кадра в один движок.
 */
const DATA_NAV: NavItem[] = [
  { to: '/registry', label: nav.registryV1, icon: Database },
  { to: '/data-quality', label: nav.dataQuality, icon: ShieldCheck },
  { to: '/data-map', label: nav.dataMap, icon: MapIcon },
];

/**
 * Меню v2 (agroai-0yt.13, docs/research/2026-10-03-design-wave2.md §5).
 * Простые названия-существительные; «Сравнение» ушло в карточку хозяйства
 * (маршрут `/compare` остаётся), «К совещанию» — печатный режим Обзора
 * (`/?mode=print`). Прежние массивы выше — это NAV_V1, выбор делает
 * `NAV_VERSION` (`VITE_NAV=v1` возвращает старое меню).
 */
const V2_TOP: NavItem[] = [
  { to: '/', label: nav.districtOverview, icon: LayoutDashboard, end: true },
  { to: '/cycles', label: nav.cycles, icon: Sprout },
  { to: '/map', label: nav.map, icon: MapIcon },
];
const V2_LAND: NavItem[] = [
  { to: '/farms', label: nav.farms, icon: Building2 },
  { to: '/water', label: nav.water, icon: Droplets },
  { to: '/quarantine', label: nav.quarantine, icon: Bug },
  { to: '/contours', label: nav.contours, icon: LandPlot },
];
const V2_REFERENCE: NavItem[] = [
  { to: '/analytics', label: nav.analyst, icon: ChartLine },
];
const V2_DATA_NAV: NavItem[] = [
  { to: '/registry', label: nav.dataSources, icon: Database },
  { to: '/data-quality', label: nav.dataQuality, icon: ShieldCheck },
  { to: '/data-map', label: nav.dataMap, icon: MapIcon },
];
const V2_GUIDE: NavItem[] = [{ to: '/guide', label: nav.guide, icon: GraduationCap }];

/** Фермерский кабинет: его уровень — своё хозяйство, станции района тут нет. */
const FARMER_NAV: NavItem[] = [
  { to: '/my/today', label: nav.today, icon: CalendarCheck },
  { to: '/my/fields', label: nav.myFields, icon: LandPlot },
  { to: '/my/history', label: nav.myReports, icon: FileText },
];

/**
 * Адреса, которые вдобавок несут ФИЛЬТР ВИДА прототипа (цикл, дата, массив,
 * хозяйство). Список узкий намеренно: фильтр вида — свойство разбора, и на
 * реестре контуров «цикл хлопка» означал бы не то же самое, что на агроцикле.
 */
const ASTRA_QUERY_ROUTES = [
  '/',
  '/season',
  '/cycles',
  '/planning',
  '/analytics',
  '/reports/briefing',
  '/ops/assignments',
  '/guide',
];

/**
 * Предзагрузка раздела (agroai-0e8.26, ADR-0020): наведение, фокус или
 * касание пункта меню грузит код ленивой страницы и повторяет опись её
 * первого экрана (`api/prefetch.ts`) — данные ложатся в память ответов под
 * своей версией и показываются экраном только после её подтверждения.
 */
function intentOf(pathname: string, search: string) {
  const data = intentHandlers(pathname, search);
  return {
    onMouseEnter: () => {
      preloadRoute(pathname);
      data.onMouseEnter();
    },
    onFocus: () => {
      preloadRoute(pathname);
      data.onFocus();
    },
    onMouseLeave: data.onMouseLeave,
    onBlur: data.onBlur,
    onTouchStart: () => {
      preloadRoute(pathname);
      data.onTouchStart();
    },
  };
}

/** Пункты меню, видимые сейчас: их описи грузятся в простое. */
const MENU_PAGES = new Map<string, { pathname: string; search: string }>();

/**
 * На каждой навигации — сразу опись ТЕКУЩЕЙ страницы (её запросы уходят
 * разом, а не волнами «ответ → следующий запрос»; первый заход после записи
 * ждёт самую долгую сборку, а не их цепочку), затем в простое — описи
 * остальных пунктов меню (без крупных ответов). Меню стоит в оболочке раньше
 * `main`, поэтому этот эффект срабатывает до эффектов страницы; эффект
 * родителя — после эффектов групп, и опись меню к этому моменту полная.
 * Повтор описи ничего не рисует: экран покажет ответ только под
 * подтверждённой версией (agroai-0e8.26, журнал №1641).
 */
function useIdleMenuPrefetch() {
  const location = useLocation();
  useEffect(() => {
    void prefetchPage(location.pathname, location.search);
    const pages = [...MENU_PAGES.values()].filter((page) => page.pathname !== location.pathname);
    return prefetchIdle(pages);
  }, [location.key, location.pathname, location.search]);
}

function NavGroup({ items, label }: { items: NavItem[]; label?: MessageDescriptor }) {
  const t = useT();
  const [params] = useSearchParams();
  const { track } = useTrack();
  const context = useAnalysisContext();
  const astraQuery = analysisNavigationQuery(params, track);

  /**
   * Срез (`as_of`, `include_demo`) едет С КАЖДЫМ переходом, а не с семью
   * избранными. Иначе человек выставляет дату состояния в полосе контекста,
   * кликает соседний раздел — и молча смотрит другой мир, причём без
   * единого признака, что мир сменился.
   */
  const search = (to: string) => {
    const query = new URLSearchParams(context.linkSearch().slice(1));
    if (ASTRA_QUERY_ROUTES.includes(to)) {
      for (const [name, value] of new URLSearchParams(astraQuery.slice(1))) {
        query.set(name, value);
      }
    }
    const text = query.toString();
    return text ? `?${text}` : '';
  };

  // Пункты группы — в опись меню для предзагрузки в простое (см. Sidebar).
  const searches = items.map((item) => search(item.to));
  const signature = items.map((item, index) => `${item.to}${searches[index]}`).join('|');
  useEffect(() => {
    const entries = items.map((item, index) => ({ pathname: item.to, search: searches[index] ?? '' }));
    for (const entry of entries) MENU_PAGES.set(entry.pathname, entry);
    return () => {
      for (const entry of entries) {
        if (MENU_PAGES.get(entry.pathname) === entry) MENU_PAGES.delete(entry.pathname);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- подпись покрывает адрес и срез
  }, [signature]);

  return (
    <>
      {label ? <div className="sidebar__group">{t(label)}</div> : null}
      {items.map((item, index) => (
        <NavLink
          key={item.to}
          to={{ pathname: item.to, search: searches[index] ?? '' }}
          end={item.end}
          title={t(item.label)}
          className={({ isActive }) => `sidebar__link ${isActive ? 'is-active' : ''}`}
          {...intentOf(item.to, searches[index] ?? '')}
        >
          <Icon icon={item.icon} size={18} />
          <span>{t(item.label)}</span>
        </NavLink>
      ))}
    </>
  );
}

/**
 * «Администрирование данных» — экраны специалиста по данным (как собран
 * канон, его замечания, геометрия). Хокиму они не нужны каждый день, поэтому
 * раздел свёрнут; открыт сам, когда человек уже стоит на одном из его экранов.
 */
function DataAdminGroup() {
  const { pathname } = useLocation();
  const t = useT();
  const v2 = NAV_VERSION === 'v2';
  const items = v2 ? V2_DATA_NAV : DATA_NAV;
  const inside = items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  return (
    <details className="sidebar__fold" open={inside || undefined}>
      <summary className="sidebar__group sidebar__foldsummary">{t(v2 ? text.groupDataSources : text.groupDataAdmin)}</summary>
      <NavGroup items={items} />
    </details>
  );
}

function PersonaBlock() {
  const navigate = useNavigate();
  const { persona, signOut } = usePersona();
  const t = useT();
  if (!persona) return null;

  const spec = roleSpec(persona.role);
  const isFarmer = persona.role === 'farmer';
  // Не вошедший смотрит район как наблюдатель: выходить ему неоткуда, и
  // кнопка предлагает противоположное — войти.
  const guest = persona.anonymous === true;

  return (
    <div className="sidebar__persona">
      <div className="sidebar__personarow">
        <span className="sidebar__personaicon" aria-hidden="true">
          <Icon icon={spec.icon} size={16} />
        </span>
        <span className="sidebar__personamain">
          {/* У фермера имя хозяйства — главное: роль без хозяйства ничего не
              говорит о том, чьи это поля. */}
          <span className="sidebar__personaname">
            {isFarmer || guest ? personaLabel(persona) : spec.title}
          </span>
          {/* Под ролью сотрудника пояснения нет (владелец 08.10): имя роли
              говорит само; фермеру — роль, гостю — как он читает район. */}
          {guest || isFarmer ? (
            <span className="sidebar__personarole">{guest ? t(text.guestRole) : spec.title}</span>
          ) : null}
        </span>
      </div>

      <button
        type="button"
        className="sidebar__switch focus-ring"
        onClick={() => {
          if (!guest) signOut();
          navigate('/login', { replace: true });
        }}
        title={t(guest ? COMMON.signIn : COMMON.signOut)}
      >
        <Icon icon={guest ? LogIn : LogOut} size={16} />
        <span>{t(guest ? COMMON.signIn : COMMON.signOut)}</span>
      </button>
    </div>
  );
}

export function Sidebar({ open = false }: { open?: boolean } = {}) {
  const { persona } = usePersona();
  const role = persona?.role ?? 'hokim';
  const isFarmer = role === 'farmer';
  const t = useT();
  useIdleMenuPrefetch();

  return (
    <aside className={`sidebar on-green${open ? ' sidebar--open' : ''}`} id="app-nav">
      <div className="sidebar__brand">
        <span className="sidebar__mark">
          <Icon icon={Sprout} size={20} />
        </span>
        <div>
          <div className="sidebar__name">Agro AI</div>
          <div className="sidebar__sub">
            {t(isFarmer ? text.subFarm : text.subStation)}
          </div>
        </div>
      </div>

      <nav className="sidebar__nav" aria-label={t(text.navLabel)}>
        {isFarmer ? (
          <NavGroup items={FARMER_NAV} />
        ) : (
          NAV_VERSION === 'v2' ? (
            <>
              <NavGroup items={V2_TOP} />
              <NavGroup label={text.groupLand} items={V2_LAND} />
              <NavGroup
                items={role === 'manager' ? OPS_NAV : role === 'inspector' || role === 'officer' ? Q_OPS_NAV : HOKIM_OPS_NAV}
                label={text.groupManagement}
              />
              <NavGroup label={text.groupReference} items={V2_REFERENCE} />
              <DataAdminGroup />
              <NavGroup label={text.groupLearning} items={V2_GUIDE} />
            </>
          ) : (
          <>
            <NavGroup items={STATION_NAV.filter(i => ['/', '/cycles', '/planning', '/analytics', '/reports/briefing'].includes(i.to))} />
            <NavGroup label={text.groupLandV1} items={[{ to: '/overview', label: nav.districtRegistry, icon: FileText }, ...STATION_NAV.filter(i => ['/map', '/farms', '/contours', '/water', '/quarantine', '/compare'].includes(i.to))]} />
            <NavGroup
              items={role === 'manager' ? OPS_NAV : HOKIM_OPS_NAV}
              label={text.groupManagement}
            />
            <DataAdminGroup />
            <NavGroup items={[{ to: "/guide", label: nav.guide, icon: GraduationCap }]} />
          </>
          )
        )}
      </nav>

      <LanguageSwitcher className="sidebar__lang" />

      <PersonaBlock />

    </aside>
  );
}
